/**
 * Success stories, couples and the check-in on the website.
 *
 * The rules live on the server (backend/services/success_stories.py); this
 * file is the one place the website talks to those endpoints and the one
 * place a story notification is routed, so the bell and the notifications
 * page cannot send the same notification to two different screens. Mirrors
 * mobile_flutter/lib/core/services/success_story_service.dart.
 */
import axios from '@/utils/axios';

export const STAGES = ['married', 'engaged', 'getting_to_know'];
export const CHANNELS = ['website', 'app', 'social', 'ads'];
export const STORY_MIN = 20;
export const STORY_MAX = 2000;
//: Checked again on the server against the bytes; this only saves an upload.
export const MEDIA_MAX_BYTES = { voice: 10 * 1024 * 1024, video: 60 * 1024 * 1024 };

export const CHECK_IN_ANSWERS = ['engaged_or_married', 'still_talking', 'did_not_work_out'];

export function mediaKind(file) {
  const type = (file?.type || '').toLowerCase();
  if (type.startsWith('audio/')) return 'voice';
  if (type.startsWith('video/')) return 'video';
  return null;
}

export async function submitStory(payload) {
  const { data } = await axios.post('/success-stories', payload);
  return data.saved;
}

export async function uploadStoryMedia(storyId, file) {
  const body = new FormData();
  body.append('file', file);
  const { data } = await axios.post(`/success-stories/${storyId}/media`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function fetchPublicStories(channel = 'website') {
  const { data } = await axios.get('/success-stories', { params: { channel } });
  return Array.isArray(data?.stories) ? data.stories : [];
}

export async function fetchCouplesCount() {
  const { data } = await axios.get('/success-stories/stats');
  return typeof data?.couples === 'number' ? data.couples : null;
}

export async function fetchMyStories() {
  const { data } = await axios.get('/success-stories/mine');
  return Array.isArray(data?.stories) ? data.stories : [];
}

export const approveFinalWording = (id) => axios.post(`/success-stories/${id}/approve-final`);
export const withdrawStory = (id) => axios.post(`/success-stories/${id}/withdraw`);

export async function fetchCouple(conversationId) {
  const { data } = await axios.get(`/conversations/${conversationId}/couple`);
  return { state: data?.state || 'none', available: data?.available === true };
}

export async function askOrConfirmCouple(conversationId) {
  const { data } = await axios.post(`/conversations/${conversationId}/couple`);
  return { state: data?.state || 'none', available: data?.available === true };
}

export async function declineCouple(conversationId) {
  const { data } = await axios.post(`/conversations/${conversationId}/couple/decline`);
  return { state: data?.state || 'none', available: data?.available === true };
}

export async function answerCheckIn(conversationId, answer) {
  const { data } = await axios.post(`/conversations/${conversationId}/check-in`, { answer });
  return { askCouple: data?.ask_couple === true, askStory: data?.ask_story === true };
}

//: Notification types this feature sends (backend services/success_story_notices.py).
export const STORY_NOTIFICATION_TYPES = [
  'couple_request', 'couple_confirmed', 'success_check_in',
  'success_story_review', 'success_story_published',
];

/** Where a story notification opens, or null when it is not one of ours. */
export function storyNotificationPath(notification) {
  const id = notification?.related_id;
  switch (notification?.type) {
    case 'couple_request':
    case 'couple_confirmed':
      return id ? `/messages?conversation=${encodeURIComponent(id)}` : '/messages';
    case 'success_check_in':
      return id ? `/messages?conversation=${encodeURIComponent(id)}&checkin=1` : '/messages';
    case 'success_story_review':
    case 'success_story_published':
      return '/settings#my-story';
    default:
      return null;
  }
}
